/* eslint-disable no-nested-ternary */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isAfter, parseISO, isBefore } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../../lib/PropTypes';
import { getImageAtSize } from '../../../lib/utils';

import { useContest } from '../../../contexts/ContestContext';
import Blocks from '../../blocks/Blocks';
import Button from '../../buttons/Button';
import StyledButton from '../../buttons/StyledButton';
import EnterPinCodeForm from '../../forms/EnterPincodeForm';
import GameItem from '../../items/Game';
import GamesList from '../../lists/Games';
import HelpModal from '../../modals/HelpModal';
import CompleteFormEntry from '../../partials/CompleteFormEntry';
import Timer from '../../partials/Timer';

import styles from '../../../../styles/themes/iga/play-iga-habs.module.scss';

import Mockup from '../../../../img/iga/2024/iga-habs-mockup.png';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    onTimerComplete: PropTypes.func,
    canPlay: PropTypes.bool,
};

const defaultProps = {
    onTimerComplete: null,
    canPlay: false,
};

function PlayPage({ page, onTimerComplete, canPlay }) {
    const route = useUrlGenerator();
    const { ended = false } = useContest() || {};
    const user = useUser();
    const { unusedPincode = null } = user || {};
    const { code = null } = unusedPincode || {};
    const {
        games: initialGames = [],
        canParticipateAt = null,
        title = null,
        description = null,
        headerImage = null,
        blocks = null,
    } = page;

    const { url: imageUrl, alt: imageAlt } = getImageAtSize(headerImage, 'medium', false) || {};

    const games = initialGames || [];
    const gamesCount = games.length;

    const topGames = useMemo(
        () =>
            games.filter(
                ({ requiresPincode = false, startAt = null }) =>
                    !requiresPincode &&
                    (startAt === null || isAfter(new Date(), parseISO(startAt))),
            ),
        [games],
    );

    const notEndedGames = useMemo(
        () =>
            games.filter(
                ({ endAt = null }) => endAt === null || isBefore(new Date(), parseISO(endAt)),
            ),
        [games],
    );

    const bonusGames = useMemo(
        () =>
            games.filter(
                ({ requiresPincode = true, startAt = null }) =>
                    requiresPincode && (startAt === null || isAfter(new Date(), parseISO(startAt))),
            ),
        [games],
    );

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    const gamesCanParticipate = useMemo(
        () =>
            (games || []).reduce((acc, game) => {
                const { canParticipate: gameCanParticipate = true } = game || {};
                if (gameCanParticipate) {
                    return true;
                }
                return acc;
            }, false),
        [games],
    );

    const showTimer = useMemo(
        () => canParticipateAtDate !== null && isAfter(canParticipateAtDate, new Date()),
        [canParticipateAtDate],
    );

    const canParticipate = canParticipateAt === null;

    const [modalOpen, setModalOpen] = useState(false);

    const openModal = useCallback(() => {
        setModalOpen(true);
    }, [setModalOpen]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const gamesEnded = notEndedGames.length === 0;

    return (
        <div className={classNames([styles.container, { [styles.gamesEnded]: gamesEnded }])}>
            <CompleteFormEntry className={styles.completeFormEntry} />

            {gamesCount > 0 && !ended ? (
                <>
                    <div className={styles.header}>
                        {imageUrl !== null ? (
                            <img className={styles.image} src={imageUrl} alt={imageAlt} />
                        ) : null}
                        {title !== null && imageUrl === null ? (
                            <h1 className={styles.title}>{title}</h1>
                        ) : null}
                        {description !== null ? <p>{description}</p> : null}
                    </div>

                    {gamesEnded ? (
                        <div className={styles.timerWrapper}>
                            <div className={styles.timerContainer}>
                                <h4 className={styles.timerTitle}>
                                    <FormattedMessage
                                        defaultMessage="The game is over."
                                        description="Timer description"
                                    />
                                </h4>
                                <p>
                                    <FormattedMessage
                                        defaultMessage="Thank you for participating, and good luck with the grand prize draw!"
                                        description="IGA label"
                                    />
                                </p>
                                <p>
                                    <FormattedMessage
                                        defaultMessage="Your special coupons are always available on the My Account page for up to 7 days after they are received!"
                                        description="IGA label"
                                    />
                                </p>
                                <StyledButton
                                    colored
                                    className={styles.timerButton}
                                    href={route('account')}
                                >
                                    <FormattedMessage
                                        defaultMessage="See my account"
                                        description="Button label"
                                    />
                                </StyledButton>
                            </div>
                        </div>
                    ) : null}

                    {showTimer ? (
                        <div className={styles.timerWrapper}>
                            <div className={styles.timerContainer}>
                                <h4 className={styles.timerTitle}>
                                    <FormattedMessage
                                        defaultMessage="That's all for today."
                                        description="Timer description"
                                    />
                                </h4>
                                <p>
                                    <FormattedMessage
                                        defaultMessage="Come back to play in:"
                                        description="IGA label"
                                    />
                                </p>
                                <Timer
                                    className={styles.timer}
                                    date={canParticipateAtDate}
                                    started
                                    onEnded={onTimerComplete}
                                />
                                <StyledButton
                                    colored
                                    className={styles.timerButton}
                                    href={route('account')}
                                >
                                    <FormattedMessage
                                        defaultMessage="See my account"
                                        description="Button label"
                                    />
                                </StyledButton>
                            </div>
                        </div>
                    ) : null}

                    {!gamesCanParticipate ? (
                        <div className={styles.timerWrapper}>
                            <div className={styles.timerContainer}>
                                <h4 className={styles.timerTitle}>
                                    <FormattedMessage
                                        defaultMessage="That's all for today."
                                        description="Timer description"
                                    />
                                </h4>
                                <p className={styles.timerDescription}>
                                    <FormattedMessage
                                        defaultMessage="Come back to play tomorrow!"
                                        description="IGA label"
                                    />
                                </p>
                            </div>
                        </div>
                    ) : null}

                    <GamesList
                        className={styles.games}
                        items={topGames}
                        canParticipate={canParticipate && canPlay && gamesCanParticipate}
                    />

                    <div className={styles.bonus}>
                        {bonusGames.length > 0 ? (
                            <div className={styles.bonusContainer}>
                                <h2 className={styles.bonusTitle}>
                                    <FormattedMessage
                                        defaultMessage="Bonus games"
                                        description="Play page title"
                                    />
                                </h2>
                                {bonusGames.map((game) => (
                                    <div
                                        key={`game=${game.id}`}
                                        className={styles.bonusGameContainer}
                                    >
                                        <div
                                            className={classNames([
                                                styles.bonusGameInner,
                                                {
                                                    [styles.disabled]:
                                                        code !== null ||
                                                        showTimer ||
                                                        !canPlay ||
                                                        !canParticipate,
                                                },
                                            ])}
                                        >
                                            <div className={styles.bonusGameTop}>
                                                <p className={styles.bonusDescription}>
                                                    <FormattedMessage
                                                        defaultMessage="Have you purchased a product targeted by the <strong>Aim for the food</strong> contest at IGA? Enter the promo code provided with your transaction receipt to play!"
                                                        description="Bonus label"
                                                    />
                                                </p>
                                                <Button
                                                    onClick={openModal}
                                                    className={styles.smallButton}
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Where can I find my code?"
                                                        description="Button label"
                                                    />
                                                </Button>
                                            </div>
                                            <EnterPinCodeForm
                                                className={styles.bonusForm}
                                                value={{ code }}
                                                submitLabel={
                                                    <FormattedMessage
                                                        defaultMessage="Confirm"
                                                        description="Button label"
                                                    />
                                                }
                                                withoutTitle
                                            />
                                        </div>
                                        <GameItem
                                            className={classNames([
                                                styles.bonusGame,
                                                {
                                                    [styles.disabled]:
                                                        code === null ||
                                                        showTimer ||
                                                        !canPlay ||
                                                        !canParticipate ||
                                                        !gamesCanParticipate,
                                                },
                                            ])}
                                            game={game}
                                        />
                                    </div>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </>
            ) : (
                <div className={styles.noGamesContainer}>
                    <div className={styles.noGames}>
                        <h1 className={styles.title}>
                            {ended ? (
                                <FormattedMessage
                                    defaultMessage="The contest has ended. Come back next time!"
                                    description="Play page title"
                                />
                            ) : null}
                        </h1>
                        <Button href={route('account')} className={styles.button}>
                            <FormattedMessage
                                defaultMessage="Go to your account"
                                description="Button label"
                            />
                        </Button>
                    </div>
                </div>
            )}

            {blocks !== null && blocks.length > 0 ? (
                <Blocks
                    isContainer
                    className={styles.blocks}
                    containerClassName={styles.blocksInner}
                    blocks={blocks}
                />
            ) : null}

            {modalOpen ? (
                <HelpModal
                    onClose={closeModal}
                    withCloseButton
                    contentClassName={styles.helpContent}
                >
                    <div className={styles.helpModal}>
                        <h3 className={styles.helpTitle}>
                            <FormattedMessage
                                defaultMessage="Promo code"
                                description="Modal title"
                            />
                        </h3>
                        <img className={styles.helpImage} src={Mockup} alt="receipt" />
                        <p className={styles.helpText}>
                            <FormattedMessage
                                defaultMessage="Buy a targeted product at a participating IGA store and enter the promo code provided with your transaction receipt!"
                                description="Modal description"
                            />
                        </p>
                    </div>
                </HelpModal>
            ) : null}
        </div>
    );
}

PlayPage.propTypes = propTypes;
PlayPage.defaultProps = defaultProps;

export default PlayPage;
